import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FieldPath } from 'react-hook-form';
import { Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/sv';

import { ApplicationState } from 'store/redux';
import { ISortState, SortService } from 'services/sortService';
import './AFSFiles.css';

export interface IAFSFile {
  title: string;
  modifiedAt: Date;
  id: string;
  category: string;
  url: string;
}

dayjs.extend(relativeTime);
dayjs.locale('sv');

export type IAFSFilesProps = {
  items?: IAFSFile[];
  onFileClick?: (url: string, filename: string) => void;
};

const AFSFiles = function ({ items, onFileClick }: IAFSFilesProps) {
  const onDownloadClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const target = event.currentTarget as HTMLAnchorElement;
    if (onFileClick) {
      onFileClick(target.href, target.innerText);
    }
  };

  const { t } = useTranslation('translation');

  const [localState, setLocalState] = useState<ISortState<IAFSFile>>({
    sortedItems: !!items ? items : [],
    sortInstruction: { value: 'title', asc: true },
    sortedColumn: 'title',
  });

  const sortService = SortService<IAFSFile>();

  useEffect(() => {
    const stateCopy = { ...localState };
    stateCopy.sortedItems = !!items ? sortService.sortItems(items, stateCopy.sortInstruction) : [];
    setLocalState(stateCopy);
  }, [items]);

  const onHeaderClick = (
    columnName: string,
    value: FieldPath<IAFSFile> | ((item: IAFSFile) => any)
  ) => {
    const stateCopy = { ...localState };
    stateCopy.sortInstruction = {
      value: value,
      asc: stateCopy.sortedColumn === columnName ? !stateCopy.sortInstruction.asc : true,
    };
    stateCopy.sortedItems = !!items ? sortService.sortItems(items, stateCopy.sortInstruction) : [];
    stateCopy.sortedColumn = columnName;
    setLocalState(stateCopy);
  };

  const getClass: (columnName: string) => string = (columnName: string) => {
    return (
      'sortable' +
      (localState.sortedColumn === columnName
        ? ' sorted' + (localState.sortInstruction.asc ? ' asc' : ' desc')
        : '')
    );
  };

  var sevenDaysAgo = dayjs(new Date()).add(-7, 'day');

  const formatTime = (time: Date) => {
    var date = dayjs(time);

    if (sevenDaysAgo > date) {
      return date.format('YYYY-MM-DD HH:mm');
    }
    return date.fromNow();
  };

  return (
    <div className="row">
      <div className="col afs-file-table-container">
        <Table hover className="afs-file-table default" striped={true}>
          <thead>
            <tr>
              <th className={getClass('title')} onClick={() => onHeaderClick('title', 'title')}>
                {t('afs.AFS_NAME')}
              </th>
              <th
                className={getClass('category')}
                onClick={() => onHeaderClick('category', 'category')}
              >
                {t('afs.AFS_CATEGORY')}
              </th>
              <th
                className={getClass('modifiedAt')}
                onClick={() => onHeaderClick('modifiedAt', 'modifiedAt')}
              >
                {t('afs.AFS_CHANGE_DATE')}
              </th>
            </tr>
          </thead>
          <tbody>
            {localState.sortedItems &&
              localState.sortedItems.map((i: IAFSFile) => (
                <tr key={i.id}>
                  <td>
                    <a onClick={onDownloadClick} href={i.url}>
                      {i.title}
                    </a>
                  </td>
                  <td>{i.category}</td>
                  <td className="timeCol">{formatTime(i.modifiedAt)}</td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default connect((appState: ApplicationState) => ({
  alert: appState.alert,
}))(AFSFiles);
